/**
 * RecentChangesBadge — small violet badge + popover on the LandingPage
 * header that surfaces the latest shipped features pulled from
 * `/api/changelog/recent-features`. Unseen-count is tracked in
 * localStorage so admins can see which items are new without a Mongo
 * round-trip per visit.
 *
 * Wired once on LandingPage for all authenticated users. Guests see
 * nothing — the landing page already gates them elsewhere.
 */
import { useEffect, useRef, useState } from "react";
import { Sparkle, CaretRight } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useT, useI18n } from "@/lib/i18n";

const SEEN_KEY = "usair_recent_features_seen_id";

export default function RecentChangesBadge() {
  const t = useT();
  const { lang } = useI18n();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [seenId, setSeenId] = useState(() => {
    try { return localStorage.getItem(SEEN_KEY) || ""; }
    catch { return ""; }
  });
  const popRef = useRef(null);

  useEffect(() => {
    api.get(`/changelog/recent-features?lang=${encodeURIComponent(lang || "en")}&limit=10`)
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]));
  }, [lang]);

  // Close on outside click
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", h);
    return () => window.removeEventListener("mousedown", h);
  }, [open]);

  if (!items.length) return null;

  // Count "unseen" = how many items appear BEFORE the seenId in the list
  // (list is newest-first). If seenId is empty, all are unseen.
  const unseenCount = seenId
    ? items.findIndex((it) => it.id === seenId)
    : items.length;
  const badge = unseenCount > 0 ? (unseenCount === -1 ? items.length : unseenCount) : 0;

  const markSeen = () => {
    const newestId = items[0]?.id;
    if (!newestId) return;
    try { localStorage.setItem(SEEN_KEY, newestId); } catch { /* storage full */ }
    setSeenId(newestId);
  };

  // iter117 — fire-and-forget click telemetry. Silently swallows errors
  // so a 500 or transient network blip never blocks the UX. Mongo
  // captures {feature_id, user_email, lang, ts}.
  const trackClick = (featureId) => {
    if (!featureId) return;
    api.post("/changelog/recent-features/track", {
      feature_id: featureId,
      lang: lang || "en",
    }).catch(() => { /* telemetry is best-effort */ });
  };

  const toggle = () => {
    setOpen((v) => {
      const next = !v;
      if (next) markSeen();
      return next;
    });
  };

  return (
    <div className="relative" ref={popRef}>
      <button
        onClick={toggle}
        data-testid="landing-recent-changes-btn"
        title={t("recent_changes.tooltip", "Recently shipped features")}
        className={`inline-flex items-center justify-center gap-1 h-8 w-8 sm:w-auto sm:px-2.5 rounded-sm border text-xs font-medium transition-colors ${
          badge > 0
            ? "border-violet-300 bg-violet-50 hover:bg-violet-100 text-violet-800"
            : "border-slate-200 hover:bg-slate-100 text-slate-500 hover:text-violet-700"
        }`}>
        <Sparkle size={13} weight={badge > 0 ? "fill" : "regular"} className={badge > 0 ? "text-violet-500" : ""} />
        <span className="hidden sm:inline">{t("recent_changes.button", "What's new")}</span>
        {badge > 0 && (
          <span
            data-testid="landing-recent-changes-count"
            className="ml-0.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-violet-600 text-white text-[10px] font-bold">
            {badge}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid="landing-recent-changes-popover"
          className="absolute right-0 mt-2 w-[380px] rounded-sm border border-slate-200 bg-white shadow-xl z-[60] overflow-hidden">
          <div className="px-4 py-2.5 border-b border-slate-100 bg-violet-50 flex items-center gap-2">
            <Sparkle size={14} weight="fill" className="text-violet-600" />
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-violet-700">
              {t("recent_changes.heading", "Recently shipped")}
            </span>
          </div>
          <ul className="max-h-[520px] overflow-y-auto divide-y divide-slate-100">
            {items.map((it) => (
              <li
                key={it.id}
                data-testid={`landing-recent-change-${it.id}`}
                onClick={() => trackClick(it.id)}
                className="px-4 py-3 hover:bg-slate-50 transition-colors cursor-pointer">
                <div className="flex items-start gap-2">
                  <CaretRight size={11} weight="bold" className="text-violet-500 shrink-0 mt-1" />
                  <div className="min-w-0">
                    <div className="text-xs font-bold text-slate-900 leading-snug">{it.title}</div>
                    <p className="text-[11px] text-slate-600 leading-relaxed mt-1 line-clamp-4">{it.summary}</p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
          <div className="px-4 py-2 border-t border-slate-100 bg-slate-50 text-[10px] text-slate-500 italic">
            {t("recent_changes.footer", "Check the Help Center for full details.")}
          </div>
        </div>
      )}
    </div>
  );
}
